import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type { MoriMessageCardData } from "./types";
import type { RecordingOpts } from "@/components/HomeFeed";
import {
  CARD_COLORS,
  bodyStyle,
  cardShellStyle,
  fullCtaStyle,
  labelStyle,
  titleStyle,
} from "./types";

interface MoriMessageCardProps {
  card: MoriMessageCardData;
  onRecord: (opts?: RecordingOpts) => void;
  markPinnedRead: (instanceId: string, contentLibraryId: string) => void;
  dismissPinnedInstance: (instanceId: string, contentLibraryId: string) => void;
  onOpenFeedback?: () => void;
  onOpenAdsPreference?: () => void;
  onOpenCollectionPreview?: () => void;
}

const CTA_LABELS: Record<string, string> = {
  capture_memory:         "Capture this moment →",
  acknowledge:            "Got it →",
  open_upgrade:           "See options →",
  open_settings:          "Settings →",
  open_mori_chat:         "Chat with Mori ✨",
  open_feedback:          "Share with Mori →",
  open_ads_preference:    "Choose →",
  open_collection_preview: "Start this journey →",
};

const MoriMessageCard = ({
  card,
  onRecord,
  markPinnedRead,
  dismissPinnedInstance,
  onOpenFeedback,
  onOpenAdsPreference,
  onOpenCollectionPreview,
}: MoriMessageCardProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const colors = CARD_COLORS.mori_message;
  const ctaLabel = CTA_LABELS[card.ctaAction] ?? "Continue →";

  const handleCTA = () => {
    markPinnedRead(card.instanceId, card.contentLibraryId);
    switch (card.ctaAction) {
      case "capture_memory":
        onRecord();
        break;
      case "acknowledge":
        // markPinnedRead already removes the card from the feed
        break;
      case "open_upgrade":
        navigate("/upgrade");
        break;
      case "open_settings":
        navigate("/profile");
        break;
      case "open_mori_chat":
        // coming soon — no-op
        break;
      case "open_feedback":
        onOpenFeedback?.();
        break;
      case "open_ads_preference":
        onOpenAdsPreference?.();
        break;
      case "open_collection_preview":
        onOpenCollectionPreview?.();
        break;
    }
  };

  const handleDismiss = () => {
    dismissPinnedInstance(card.instanceId, card.contentLibraryId);
  };

  return (
    <div style={{ position: "relative", ...cardShellStyle(colors.bg, colors.border) }}>
      {/* Dismiss button */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t("aria.dismiss")}
        style={{
          position: "absolute", top: 10, right: 10,
          width: 20, height: 20, borderRadius: "50%", border: "none",
          background: "rgba(122,74,16,0.10)", color: "rgba(122,74,16,0.50)",
          fontSize: 12, lineHeight: 1, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >
        ×
      </button>

      {/* Card content */}
      <div style={{ flex: 1, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* Mori label */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
          <span style={{ fontSize: 16 }}>🌰</span>
          <span style={labelStyle(colors.body)}>Mori</span>
        </div>

        {/* Optional title */}
        {card.title && (
          <p
            style={{
              ...titleStyle(colors.title),
              marginBottom: 6,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {card.title}
          </p>
        )}

        {/* Body — already interpolated */}
        <p
          style={{
            ...bodyStyle(colors.body),
            flex: 1,
            display: "-webkit-box",
            WebkitLineClamp: card.title ? 5 : 7,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {card.body}
        </p>
      </div>

      {/* CTA */}
      <div style={{ paddingTop: 12 }}>
        <button
          type="button"
          onClick={handleCTA}
          style={fullCtaStyle(colors.title, colors.bg)}
        >
          {ctaLabel}
        </button>
      </div>
    </div>
  );
};

export default MoriMessageCard;
